import { useContext, useState } from 'react';
import { Notification, toast } from 'components/ui';
import { ScheduledPostContext } from 'contexts/ScheduledPostContext';
import { notifyPostGroupSaved } from 'utils/postSaveNotifier';
import { getPostGroupStatus } from './getPostGroupStatus';
import {
    useUpdatePostGroup,
    useDeletePostGroup,
    usePublishPostGroup,
} from 'api/hooks/usePostGroups';

/**
 * Toasts the outcome of "Publish now": one POST /postGroups/{id}/publish per network, settled
 * together. A plain success has no body (204). A 202 carries {message}: bulk-imported media is
 * still downloading, so those posts stay pending and the API publishes them by itself once the
 * downloads finish. A failed call shows the API's own message. Identical messages (two networks
 * waiting on the same downloads, or failing the same way) are shown once.
 *
 * @param {PromiseSettledResult[]} results - from Promise.allSettled, one per network
 */
export const toastPublishNowResults = (results = []) => {
    const waiting = new Set();
    const errors = new Set();
    let published = 0;
    results.forEach(({ status, value, reason }) => {
        if (status === 'rejected') {
            errors.add(reason?.response?.data?.message || reason?.message || 'Could not publish the post.');
        } else if (typeof value?.message === 'string' && value.message) {
            waiting.add(value.message);
        } else {
            published += 1;
        }
    });

    if (published > 0 || (!waiting.size && !errors.size)) {
        toast.push(<Notification title="Post is being published..." type="info" />);
    }
    waiting.forEach((message) => toast.push(<Notification title={message} type="info" />));
    errors.forEach((message) => toast.push(<Notification title={message} type="danger" />));
};

/**
 * Provides the post-group action handlers that PostArticleButton expects
 * (publish / schedule / draft toggle) plus a delete handler, without pulling in
 * the full PostArticle component. Intended for the compact list view.
 *
 * Backed by the React Query mutation hooks so success automatically invalidates
 * every cached postGroups/posts list across the app.
 *
 * @param {Object} postGroup - the post group (API shape)
 * @param {Object} options
 * @param {(freshPostGroup: Object) => void} [options.onUpdated]
 */
export const usePostGroupActions = (postGroup, { onUpdated } = {}) => {
    const { getScheduledPostGroup, triggerUpdate } = useContext(ScheduledPostContext);
    const [isLoading, setIsLoading] = useState(false);

    const { mutateAsync: updatePostGroup } = useUpdatePostGroup();
    const { mutateAsync: deletePostGroup } = useDeletePostGroup();
    const { mutateAsync: publishPostGroup } = usePublishPostGroup();

    const status = getPostGroupStatus(postGroup);

    const refreshFromDb = async () => {
        if (!postGroup?.id) return;
        try {
            const fresh = await getScheduledPostGroup(postGroup.id);
            if (fresh) onUpdated?.(fresh);
        } catch (err) {
            /* keep optimistic state if the refresh fails */
        }
        triggerUpdate?.();
    };

    const updateFieldValuePostGroup = async (field, value) => {
        if (!postGroup?.id || isLoading) return;
        setIsLoading(true);
        try {
            await updatePostGroup({ id: postGroup.id, [field]: value });
            toast.push(<Notification title="Post updated successfully!" type="success" />);
            await refreshFromDb();
        } catch (err) {
            toast.push(<Notification title={err?.message || 'Could not update the post'} type="danger" />);
        }
        setIsLoading(false);
    };

    const onPostNow = async () => {
        if (!postGroup?.id || isLoading) return;
        setIsLoading(true);
        try {
            const unpublishedPosts = (postGroup?.posts || []).filter(
                (p) => p?.status == 'pending' || p?.status == 'error'
            );
            const uniqueNetworkTypes = [
                ...new Set(unpublishedPosts.map((post) => post.social_network_type)),
            ];

            if (!uniqueNetworkTypes.length) {
                toast.push(<Notification title="There are no posts to publish" type="info" />);
            } else {
                const results = await Promise.allSettled(
                    uniqueNetworkTypes.map((networkType) =>
                        publishPostGroup({
                            id: postGroup.id,
                            social_network_type: networkType,
                            update_publish_at: true,
                        })
                    )
                );
                toastPublishNowResults(results);
                notifyPostGroupSaved(postGroup.id, {
                    postCount: postGroup?.posts?.length,
                    action: 'published',
                });
                await refreshFromDb();
            }
        } catch (err) {
            toast.push(<Notification title={err?.message || err} type="danger" />);
        }
        setIsLoading(false);
    };

    const onDeletePostGroup = async () => {
        if (!postGroup?.id) return;
        await deletePostGroup(postGroup.id);
        triggerUpdate?.();
    };

    return {
        status,
        isLoading,
        onPostNow,
        updateFieldValuePostGroup,
        onDeletePostGroup,
        refreshFromDb,
    };
};

export default usePostGroupActions;
